import React, { FormEvent, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../ui/toast';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { enrollBegin, enrollConfirm } from '../../utils/api_service/mfa_api';
import { hasSession, storeSession } from '../../utils/auth/session';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { clearMfaGate } from '../../utils/auth/mfaGate';
import { clearPending, takeToken, urlCarriesToken } from '../../utils/auth/mfaHandoff';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';

type Stage = 'loading' | 'setup' | 'codes';

/**
 * Required two-factor enrolment. Reached three ways, all of which end in a
 * session that records the second factor:
 *  - password login returned mfaEnrollmentRequired (token in sessionStorage);
 *  - an SSO login was redirected here by the backend (?token=&next=);
 *  - an already signed-in user hit the in-app gate because the tenant now
 *    requires MFA (no token: the session bearer identifies them).
 */
export const MfaEnroll = () => {
  const history = useHistory();
  const [{ token: enrollToken, next }] = useState(() => takeToken('enroll'));
  const signedIn = hasSession();
  const [stage, setStage] = useState<Stage>('loading');
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [enrollmentId, setEnrollmentId] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!enrollToken && !signedIn) { history.push('/login'); return; }
    if (urlCarriesToken()) history.replace('/auth/mfa/enroll');
    enrollBegin(enrollToken ? { enrollToken } : {})
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) { toast.error('Could not start setup.', { position: 'bottom-center' }); return; }
        setSecret(data.secret);
        setOtpauthUri(data.otpauthUri || '');
        setEnrollmentId(data.enrollmentId);
        setStage('setup');
      })
      .catch(() => toast.error('Could not start setup.', { position: 'bottom-center' }));
    // eslint-disable-next-line
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    const res = await enrollConfirm({ enrollmentId, code, enrollToken: enrollToken || undefined });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast.error('That code was not accepted.', { position: 'bottom-center' });
      return;
    }
    setRecoveryCodes(data.recoveryCodes || []);
    // Both the forced (login) and in-app paths return a session that carries
    // the second factor; store it so the gate and the refresh backstop clear.
    if (data.accessToken) storeSession(data);
    setStage('codes');
  };

  const finish = async () => {
    clearPending('enroll');
    clearMfaGate();
    // Session was already stored on confirm; hydrate + route to the deep link.
    history.push(await finishSignIn(undefined, { deepLink: next }));
  };

  if (!enrollToken && !signedIn) return null;

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <div className='flex flex-col w-full gap-4 pt-10'>
        <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'
             role='status'>
          <strong>Two-factor authentication is required.</strong> Your organisation requires every
          employee account to use two-factor authentication. Set it up now to continue.
        </div>

        {stage === 'loading' && <p className='text-slate-500'>Preparing two-factor setup…</p>}

        {stage === 'setup' && (
          <form className='flex flex-col gap-4' onSubmit={confirm}>
            <h2 className='text-2xl font-semibold text-slate-900'>Set up two-factor authentication</h2>
            <p className='text-sm text-slate-500'>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password), or
              enter the key manually, then type the 6-digit code it shows.
            </p>
            {otpauthUri && (
              <div className='self-start rounded-lg border border-slate-200 bg-white p-3'>
                <QRCodeSVG value={otpauthUri} size={168} />
              </div>
            )}
            <div className='rounded-lg bg-slate-100 p-3 font-mono text-sm tracking-widest break-all'>{secret}</div>
            <TextInput type='text' name='code' label='Authentication code' value={code}
                       onChange={(e) => setCode(e.target.value.trim())} />
            <Button type='submit' label='Verify and enable' />
          </form>
        )}

        {stage === 'codes' && (
          <div className='flex flex-col gap-4'>
            <h2 className='text-2xl font-semibold text-slate-900'>Save your recovery codes</h2>
            <p className='text-sm text-slate-500'>
              Each code can be used once if you lose your authenticator. Store them somewhere safe — they will not
              be shown again.
            </p>
            <ul className='grid grid-cols-2 gap-2 font-mono text-sm'>
              {recoveryCodes.map((c) => <li key={c} className='rounded bg-slate-100 px-2 py-1'>{c}</li>)}
            </ul>
            <Button onClick={finish} label='I have saved my codes' />
          </div>
        )}
      </div>
    </AuthenticationUiLayout>
  );
};
